import { useEffect, useState } from "react";

import { parseVTT, type VttSegment } from "../utils/vttParser";

export type { VttSegment };

export interface TranscriptData {
  type: "txt" | "vtt";
  rawContent: string;
  textContent: string;
  segments?: VttSegment[];
}

interface UseTranscriptResult {
  data: TranscriptData | null;
  isLoading: boolean;
  error: Error | null;
}

interface UseTranscriptOptions {
  enabled?: boolean;
  onError?: (error: Error) => void;
}

export const useTranscript = (
  url: string | null,
  type: "txt" | "vtt",
  options: UseTranscriptOptions = {},
): UseTranscriptResult => {
  const { enabled = true, onError } = options;
  const [data, setData] = useState<TranscriptData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!url || !enabled) {
      setData(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    const fetchTranscript = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(url);

        if (!response.ok) {
          console.error(
            "Failed to fetch transcript: ",
            response.status,
            response.statusText,
          );
          throw new Error(
            `Failed to fetch transcript: ${response.status} ${response.statusText}`,
          );
        }

        const rawContent = await response.text();

        if (type === "vtt") {
          const segments = parseVTT(rawContent);
          const textContent = segments
            .map((segment) => segment.text)
            .join("\n");

          setData({
            type: "vtt",
            rawContent,
            textContent,
            segments,
          });
        } else {
          setData({
            type: "txt",
            rawContent,
            textContent: rawContent,
          });
        }
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Unknown error occurred");
        setError(error);
        if (error) onError?.(error);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTranscript();
  }, [url, type, enabled]);

  return { data, isLoading, error };
};
